import type { Dayjs } from "@eisox/dayjs";

import type { DateRange } from "../../Calendar";
import type { DateFieldUnit, RangePart } from "../date-field.type";

interface Bounds {
  min?: Dayjs;
  max?: Dayjs;
}

interface RangeAssignment {
  range: DateRange;
  activePart: RangePart;
}

const clampToBounds = (value: Dayjs, min?: Dayjs, max?: Dayjs): Dayjs => {
  if (min && value.isBefore(min)) return min;
  if (max && value.isAfter(max)) return max;
  return value;
};

const toPeriodPart = (value: Dayjs, unit: DateFieldUnit, part: RangePart, { min, max }: Bounds = {}): Dayjs => {
  if (unit === "day") return value;
  const edge = part === "from" ? value.startOf(unit) : value.endOf(unit).startOf("day");
  return clampToBounds(edge, min, max);
};

const isOutOfBounds = (value: Dayjs | null, min?: Dayjs, max?: Dayjs): boolean =>
  !!value && ((!!min && value.isBefore(min)) || (!!max && value.isAfter(max)));

const isRangeInverted = (range: DateRange): boolean => !!range.from && !!range.to && range.to.isBefore(range.from);

const nextPartToFill = (range: DateRange): RangePart => (range.from && !range.to ? "to" : "from");

const assignRangePart = (
  range: DateRange,
  part: RangePart,
  value: Dayjs | null,
  bounds: Bounds = {},
): RangeAssignment => {
  if (!value) return { range: { ...range, [part]: null }, activePart: part };

  const clamped = clampToBounds(value, bounds.min, bounds.max);

  if (part === "from") {
    return {
      range: { from: clamped, to: range.to && clamped.isAfter(range.to) ? null : range.to },
      activePart: "to",
    };
  }

  if (range.from && clamped.isBefore(range.from)) return { range: { from: clamped, to: null }, activePart: "to" };

  return { range: { from: range.from, to: clamped }, activePart: "to" };
};

const assignRangePeriod = (
  range: DateRange,
  part: RangePart,
  period: Dayjs,
  unit: DateFieldUnit,
  bounds: Bounds = {},
): RangeAssignment => {
  const start = toPeriodPart(period, unit, "from", bounds);
  const end = toPeriodPart(period, unit, "to", bounds);
  const startsOver = part === "from" || (!!range.from && end.isBefore(range.from));
  return assignRangePart(range, part, startsOver ? start : end, bounds);
};

const isRangeComplete = (range: DateRange): boolean => !!range.from && !!range.to;

export {
  assignRangePart,
  assignRangePeriod,
  clampToBounds,
  isOutOfBounds,
  isRangeComplete,
  isRangeInverted,
  nextPartToFill,
  toPeriodPart,
};
export type { RangeAssignment };
